import { NormalizedNumber } from '@sparkdotfi/common-universal'
import { AlertTriangle } from 'lucide-react'

import { MobileViewOptions } from '../types'
import { Token } from '@/domain/types/Token'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/ui/atoms/tooltip/Tooltip'
import { TokenAmountTooltip } from '@/ui/organisms/token-amount-tooltip/TokenAmountTooltip'
import { UsdAmountTooltip } from '@/ui/organisms/usd-amount-tooltip/UsdAmountTooltip'
import { cn } from '@/ui/utils/style'

export interface CompactAmountCellFormattingOptions {
  style?: 'auto' | 'compact'
  dimmed?: boolean
  zeroAmountHandling?: 'show-zero' | 'show-dash'
  showUsdValue?: boolean
  showSymbol?: 'always' | 'only-tooltip-trigger' | 'only-tooltip-content' | 'never'
}

interface AmountCellProps {
  token: Token
  amount: NormalizedNumber
  formattingOptions?: CompactAmountCellFormattingOptions
  mobileViewOptions?: MobileViewOptions
  alertTooltip?: string
  iconSrc?: string
  className?: string
  'data-testid'?: string
}

export function AmountCell({
  amount,
  token,
  formattingOptions,
  mobileViewOptions,
  alertTooltip,
  iconSrc,
  className,
  'data-testid': dataTestId,
}: AmountCellProps) {
  if (mobileViewOptions?.isMobileView) {
    return (
      <div className="flex flex-row items-center justify-between">
        <div className="typography-label-4 w-full text-secondary">{mobileViewOptions.rowTitle}</div>
        <div className="flex items-center gap-2">
          {alertTooltip && <AlertTooltip text={alertTooltip} />}
          <Amount
            token={token}
            amount={amount}
            iconSrc={iconSrc}
            formattingOptions={formattingOptions}
            className={className}
            data-testid={dataTestId}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="flex items-center justify-end gap-2">
      {alertTooltip && <AlertTooltip text={alertTooltip} />}
      <Amount
        token={token}
        amount={amount}
        iconSrc={iconSrc}
        formattingOptions={formattingOptions}
        className={className}
        data-testid={dataTestId}
      />
    </div>
  )
}

function AlertTooltip({ text }: { text: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <AlertTriangle size={14} className="text-system-error-primary" />
      </TooltipTrigger>
      <TooltipContent>{text}</TooltipContent>
    </Tooltip>
  )
}

interface CompactValueProps {
  token: Token
  amount: NormalizedNumber
  iconSrc?: string
  className?: string
  'data-testid'?: string
  formattingOptions?: CompactAmountCellFormattingOptions
}

function Amount({
  token,
  amount,
  formattingOptions,
  iconSrc,
  className,
  'data-testid': dataTestId,
}: CompactValueProps) {
  const {
    zeroAmountHandling = 'show-dash',
    style = 'auto',
    dimmed = false,
    showUsdValue = true,
    showSymbol = 'never',
  } = formattingOptions ?? {}

  if (amount.isZero() && zeroAmountHandling === 'show-dash') {
    return (
      <div className={cn('flex w-full flex-row justify-end', dimmed && 'text-secondary/70')} data-testid={dataTestId}>
        —
      </div>
    )
  }

  return (
    <div className={cn('flex flex-col', className)} data-testid={dataTestId}>
      <div className="flex items-center justify-end gap-0.5 md:gap-1.5">
        {iconSrc && <img src={iconSrc} className="icon-size-xs" alt="Amount icon" />}
        <div
          className={cn(
            'typography-label-2 flex w-full flex-row justify-end text-primary',
            dimmed && 'text-neutral-600/70',
            className,
          )}
        >
          <TokenAmountTooltip token={token} amount={amount} options={{ style, showSymbol }} />
        </div>
      </div>
      {showUsdValue && (
        <div className="flex w-full flex-row justify-end">
          <div className={cn('typography-body-4 text-secondary', dimmed && 'text-neutral-600/30')}>
            <UsdAmountTooltip token={token} amount={amount} style={style} />
          </div>
        </div>
      )}
    </div>
  )
}
